import React, { useState } from 'react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import { Plus, List, Users, Pause, Play, Trash2, Edit2, X } from 'lucide-react';

export default function QueuesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  useEscapeKey(showForm, () => { setShowForm(false); reset(); });
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset } = useForm();

  const { data = [], isLoading } = useQuery('queues', () => api.get('/queues').then(r => r.data));

  const saveMut = useMutation(
    (d) => editing ? api.put('/queues/' + editing.id, d) : api.post('/queues', d),
    { onSuccess: () => { qc.invalidateQueries('queues'); toast.success('Cola guardada'); setShowForm(false); reset(); setEditing(null); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error') }
  );

  const delMut = useMutation((id) => api.delete('/queues/' + id),
    { onSuccess: () => { qc.invalidateQueries('queues'); toast.success('Cola eliminada'); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error') });

  const pauseMut = useMutation(
    ({ queueId, memberId, paused }) => api.put('/queues/' + queueId + '/members/' + memberId + '/pause', { paused }),
    { onSuccess: () => { qc.invalidateQueries('queues'); toast.success('Agente actualizado'); } }
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Colas ACD</h1>
          <p className="text-gray-400 text-sm">{data.length} colas configuradas</p></div>
        <button onClick={() => setShowForm(true)} className="btn-primary"><Plus size={18} /> Nueva Cola</button>
      </div>

      {isLoading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : (
        <div className="space-y-4">
          {data.map(queue => (
            <div key={queue.id} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
                    <List size={18} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{queue.name}</div>
                    <div className="text-xs text-gray-400">Ext. {queue.number} | Estrategia: {queue.strategy} | Timeout: {queue.timeout}s</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className={queue.status === 'active' ? 'badge-success' : 'badge-danger'}>{queue.status}</span>
                  <button onClick={async () => { if (await confirm({ title: `Eliminar la cola ${queue.name}`, message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true, checkCalls: true })) delMut.mutate(queue.id); }}
                    aria-label={`Eliminar cola ${queue.name}`}
                    className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg"><Trash2 size={15} /></button>
                </div>
              </div>

              {queue.members && queue.members.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted mb-2 flex items-center gap-1.5">
                    <Users size={12} /> AGENTES ({queue.members.length})
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {queue.members.map(member => (
                      <div key={member.id} className="bg-gray-800/60 rounded-lg p-2.5 flex items-center gap-2">
                        <span className={'status-dot ' + (member.paused ? 'dnd' : 'online')} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-200 truncate">{member.name}</div>
                          <div className="text-xs text-muted">Ext. {member.number}</div>
                        </div>
                        <button onClick={() => pauseMut.mutate({ queueId: queue.id, memberId: member.id, paused: !member.paused })}
                          aria-label={member.paused ? `Reanudar a ${member.name}` : `Pausar a ${member.name}`}
                          className="text-muted hover:text-yellow-400">
                          {member.paused ? <Play size={12} /> : <Pause size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {!data.length && <div className="card text-center py-12 text-muted">Sin colas configuradas.</div>}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div role="dialog" aria-modal="true" className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">Nueva Cola ACD</h2>
              <button onClick={() => { setShowForm(false); reset(); }} aria-label="Cerrar"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit(d => saveMut.mutate(d))} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label" htmlFor="queue-number">Numero (700-799) *</label>
                  <input id="queue-number" {...register('number', { required: true, pattern: /^7\d{2}$/ })} className="input" placeholder="700" /></div>
                <div><label className="label" htmlFor="queue-name">Nombre *</label>
                  <input id="queue-name" {...register('name', { required: true })} className="input" placeholder="Soporte" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label" htmlFor="queue-strategy">Estrategia</label>
                  <select id="queue-strategy" {...register('strategy')} className="input">
                    <option value="ringall">Ring All</option>
                    <option value="leastrecent">Menos reciente</option>
                    <option value="fewestcalls">Menos llamadas</option>
                    <option value="random">Aleatorio</option>
                    <option value="rrmemory">Round Robin</option>
                    <option value="linear">Lineal</option>
                  </select></div>
                <div><label className="label" htmlFor="queue-timeout">Timeout (seg)</label>
                  <input id="queue-timeout" {...register('timeout')} type="number" className="input" defaultValue={30} min={5} max={300} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label" htmlFor="queue-wrapup">Tiempo de wrap (seg)</label>
                  <input id="queue-wrapup" {...register('wrapupTime')} type="number" className="input" defaultValue={5} /></div>
                <div><label className="label" htmlFor="queue-maxlen">Max en espera (0=ilimitado)</label>
                  <input id="queue-maxlen" {...register('maxLen')} type="number" className="input" defaultValue={0} /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); reset(); }} className="btn-secondary flex-1 justify-center">Cancelar</button>
                <button type="submit" className="btn-primary flex-1 justify-center">Crear Cola</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
